import { useRef, useState, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ReplyIcon, ReactIcon } from '../Icons';
import EmojiPicker from '../EmojiPicker';
import QuickReactionPicker from './QuickReactionPicker';

interface MessageActionBarProps {
  onReply: () => void;
  onReact: (emoji: string) => void;
  onReport?: () => void;
  modSlot?: ReactNode;
  align: 'start' | 'end';
}

export default function MessageActionBar({ onReply, onReact, onReport, modSlot, align }: MessageActionBarProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const reactButtonRef = useRef<HTMLButtonElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  const closePickers = useCallback(() => {
    setShowPicker(false);
    setShowFullPicker(false);
    setPortalTarget(null);
  }, []);

  const handleSelect = (emoji: string) => {
    onReact(emoji);
    closePickers();
  };

  const handleOpenFull = () => {
    const container = barRef.current?.closest('.message-swipe-container');
    setPortalTarget(container instanceof HTMLElement ? container : null);
    setShowFullPicker(true);
  };

  const handleReactToggle = () => {
    if (showFullPicker) {
      closePickers();
      return;
    }
    setShowPicker((open) => !open);
  };

  return (
    <div ref={barRef} className={`message-action-bar align-${align}`}>
      <button
        type="button"
        className="message-action-btn"
        onClick={onReply}
        title="Répondre"
        aria-label="Répondre"
      >
        <ReplyIcon size={18} />
      </button>
      {onReport && (
        <button
          type="button"
          className="message-action-btn"
          onClick={onReport}
          title="Signaler"
          aria-label="Signaler"
        >
          ⚑
        </button>
      )}
      {modSlot}
      <div className="message-action-react-wrap">
        <button
          ref={reactButtonRef}
          type="button"
          className={`message-action-btn ${showPicker || showFullPicker ? 'active' : ''}`}
          onClick={handleReactToggle}
          title="Réagir"
          aria-label="Réagir"
        >
          <ReactIcon size={18} />
        </button>
        <QuickReactionPicker
          isOpen={showPicker && !showFullPicker}
          onClose={closePickers}
          onSelect={handleSelect}
          onOpenFull={handleOpenFull}
          anchorRef={reactButtonRef}
          align={align}
        />
      </div>

      {showFullPicker && portalTarget && createPortal(
        <EmojiPicker
          isOpen
          onClose={closePickers}
          onSelect={handleSelect}
          anchorRef={reactButtonRef}
          closeOnSelect
          variant="reaction"
        />,
        portalTarget,
      )}
    </div>
  );
}
